export type WebStoreFormat = "json" | "raw";
export type WebStoreOptions = {
  format?: WebStoreFormat;
};
export type JsonStoreOptions = {
  format?: "json";
};
export type RawStoreOptions = {
  format: "raw";
};

type RawDefault = string | null | undefined;

export function createWebStore(instance: () => Storage) {
  function find<T>(
    keys: string[],
    defaultValue: T,
    options?: JsonStoreOptions
  ): [string, T];
  function find<Default extends RawDefault>(
    keys: string[],
    defaultValue: Default,
    options: RawStoreOptions
  ): [string, string | Default];
  function find(
    keys: string[],
    defaultValue: unknown,
    options?: WebStoreOptions
  ): [string, unknown] {
    const storage = instance();
    for (const key of keys) {
      const value = storage.getItem(key);
      if (value !== null) {
        if (options?.format === "raw") return [key, value];
        try {
          return [key, JSON.parse(value)];
        } catch {
          continue;
        }
      }
    }
    return [keys[0], defaultValue];
  }

  function get<T>(key: string, defaultValue: T, options?: JsonStoreOptions): T;
  function get<Default extends RawDefault>(
    key: string,
    defaultValue: Default,
    options: RawStoreOptions
  ): string | Default;
  function get(
    key: string,
    defaultValue: unknown,
    options?: WebStoreOptions
  ): unknown {
    const data = instance().getItem(key);
    if (!data) return defaultValue;
    try {
      return options?.format === "raw" ? data : JSON.parse(data);
    } catch {
      return defaultValue;
    }
  }

  function set<T>(key: string, data: T, options?: JsonStoreOptions): void;
  function set(key: string, data: string, options: RawStoreOptions): void;
  function set(key: string, data: unknown, options?: WebStoreOptions): void {
    if (options?.format === "raw") {
      if (typeof data !== "string") {
        throw new TypeError("Raw storage values must be strings");
      }
      instance().setItem(key, data);
      return;
    }
    instance().setItem(key, String(JSON.stringify(data)));
  }

  return {
    instance,
    find,
    has(key: string): boolean {
      return instance().getItem(key) !== null;
    },
    keysWithPrefix(prefix: string): string[] {
      const storage = instance();
      const out: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key !== null && key.startsWith(prefix)) out.push(key);
      }
      return out;
    },
    get,
    getJson<T>(key: string, defaultValue: T): T {
      return get(key, defaultValue);
    },
    getRaw<Default extends RawDefault>(
      key: string,
      defaultValue: Default
    ): string | Default {
      return get(key, defaultValue, { format: "raw" });
    },
    set,
    setJson<T>(key: string, data: T): void {
      set(key, data);
    },
    setRaw(key: string, data: string): void {
      set(key, data, { format: "raw" });
    },
    unset(key: string): void {
      instance().removeItem(key);
    },
  };
}
